import {autoinject, Container} from "aurelia-dependency-injection";
import {SearchView} from "../search/views/search";
import {SearchModel} from "../search/models/search";
import {SearchBoxView} from "../search/views/search-box";
import {SearchBoxModel} from "../search/models/search-box";
import {MasterView} from "../shared/views/master-view";
import {IProductFilter} from "../search/interfaces/product-filter";
import {AppEvents} from "../app-events";
import {SearchTreeBuilder} from "./helpers/search-tree/search-tree-builder";
require("../search/styles/search.less");
require("../search/styles/auto-complete.less");
import {ApplicationInsights} from "../shared/helpers/application-insights";
import {AppSettings} from "../app-settings";
import { AuthService } from "../authentication/auth-service";

@autoinject
export class SearchModule extends Backbone.Model {
    public active: boolean;

    constructor(
        private appEvents: Backbone.Events,
        private searchView: SearchView,
        public searchModel: SearchModel,
        private searchBoxView: SearchBoxView,
        private searchBoxModel: SearchBoxModel,
        private masterView: MasterView,
        private appInsights: ApplicationInsights,
        private authService: AuthService) {
            super();
            this.setupViews();
            this.setupModelChangeListeners();
            this.setupEvents();
    }

    public welcome() {
        this.show();
        if (this.searchModel.AllResults.length === 0) {
            this.searchView.hideSearchControls();
        }
    }

    public search(query = "", page = 1, order = "Relevance", availableDate?, materialTypes = [], includedCategoryFilters = [], includedProductFilters: IProductFilter[] = null, showFullTextOnly = false) {
        return new Promise(async (resolve, reject) => {
            try {
                // set whether browser refresh was clicked
                const browserRefresh = (this.searchModel.TotalResults === -1);

                this.show();
                this.searchBoxModel.cancelSuggestionRequest();
                // in case this was from someone navigating to the search page, de-encode the asterisk
                query = query.replace("{asterisk}", "*");
                this.searchModel.HighlightableSearchFragments = [];

                if (order !== null) { this.searchModel.setOrder(order, false); }

                if (availableDate !== null) { this.searchModel.setAvailableDate(availableDate, false); }

                if (query !== null) { this.searchModel.constructSearchTree(query); }

                if (materialTypes) { this.searchModel.includeMaterialFilters(materialTypes, false); }

                this.searchModel.includeCategoryFilters(includedCategoryFilters);
                if (page !== null) { this.searchModel.setPage(page, false); }
                this.searchModel.ShowFulltextDocsOnly = showFullTextOnly;
                this.searchModel.Loading = true;

                await this.searchModel.updateResults();

                if (browserRefresh) {
                    this.appEvents.trigger(AppEvents.PageRefresh);
                }
                resolve();
            } catch (e) {
                console.error(e);
                // appInsights.trackException(e);
                this.searchModel.Loading = false;
                const options: any = { ErrorMessage: e.message };
                this.searchModel.trigger(AppEvents.SearchServerRequestFailed, options);
                reject();
            }
        });
    }

    public show(): void {
        this.closeAdvanced();
        this.searchModel.AdvancedVisible = false;
        this.searchModel.ErrorMessage = "";
        this.searchBoxModel.AdvancedVisible = false;
        const searchRegion = this.masterView.getRegion("searchRegion");
        if (searchRegion.el) {
            this.searchView.$el.show();
        }
        this.active = true;
        this.searchBoxView.placeCaretAtEnd();
    }

    public hide(): void {
        this.closeAdvanced();
        this.active = false;
        const searchRegion = this.masterView.getRegion("searchRegion");
        this.appEvents.trigger(AppEvents.HideSuggestions);
        this.searchView.$el.hide();
    }

    /** Resets the view to a new search */
    public reset(): void {
        this.searchModel.cleanOut();
        this.searchBoxModel.cleanOut();
        this.searchBoxView.updateExpandedSearchWidgetContent();
        this.welcome();
    }

    // This is from an event inside the application
    public closeAdvancedSearch() {
        let callback = this.searchBoxModel.AdvancedCloseCallback || "search";
        callback = callback.replace((Backbone.history as any).root, "");
        if (callback.indexOf("browse") !== -1) { callback = "search"; }
        Backbone.history.navigate(callback, { trigger: true });
        this.appEvents.trigger(AppEvents.CloseAdvanced);
    }

    public closeAdvanced() {
        $("#viewport-content").removeClass("advanced-search");
        $("#error-region").hide();
        this.searchBoxModel.AdvancedVisible = false;
    }

    public async showAdvancedSearch(formID?: string) {
        $("#viewport-content").addClass("advanced-search");
        const searchRegion = this.masterView.getRegion("searchRegion");
        if ((searchRegion as any).$el) {
            this.searchView.$el.show();
            (searchRegion as any).$el.removeClass("vanish");
        }
        this.searchModel.AdvancedVisible = true;
        this.searchBoxModel.AdvancedVisible = true;
        await this.searchView.setupAdvancedSearchView(formID);
        if (!this.searchModel.SearchTree) {
            const tree = SearchTreeBuilder.buildTreeFromFields(this.searchModel.AdvancedSearch.getAdvancedFormData());
            this.searchModel.SearchTree = tree;
        }
        this.searchModel.AdvancedSearch.populateFields(this.searchModel.SearchTree.toDataObject());
        this.searchModel.AdvancedSearch.AdvancedSearchEverOpened = true;
    }

    private setupModelChangeListeners() {
        this.listenTo(this.searchBoxModel, "searchSubmit", this.searchSubmit);
        this.listenTo(this.searchModel, "searchSubmit", this.searchSubmit); // Treat searches the same whether they come from adv. search or quick
        this.listenTo(this.searchModel, "Search:ServerRequestComplete", this.searchComplete);
        this.listenTo(this.searchModel, "closeAdvancedSearch", this.closeAdvancedSearch);
        this.listenTo(this.searchModel.AdvancedSearch, "KeyPressed", this.advancedFormKeyPressed);
        this.listenTo(this.searchBoxModel, "change:UserQuery", this.userQueryChanged);
        this.listenTo(this.searchModel, "change:QueryChanged", this.treeChanged);
        this.listenTo(this.authService.AppUser, "refresh", this.userChanged);
    }

    private setupEvents() {
        this.appEvents.on(AppEvents.ResetSearch, () => {
            this.reset();
        });
    }

    private userChanged() {
        this.searchView.model.AdvancedSearch.load(null);
    }

    private treeChanged(model, prop) {
        const tree = this.searchModel.SearchTree;
        if (tree) {
            this.searchBoxModel.setUserQueryFromTree(tree, true);
        }
    }

    private advancedFormKeyPressed(model, prop) {
        const tree = SearchTreeBuilder.buildTreeFromFields(this.searchModel.AdvancedSearch.getAdvancedFormData());

        this.searchModel.SearchTree = tree;
        this.searchBoxModel.setUserQueryFromTree(tree, true); // set it silently as we don't want it to loop or change the ASF as we're typing

    }
    private userQueryChanged(model, prop) {

        const query = this.searchBoxModel.UserQuery;
        const tree = SearchTreeBuilder.buildTreeFromQuery(query);
        this.searchModel.SearchTree = tree;
        const data = tree.toDataObject();

        this.searchModel.AdvancedSearch.populateFields(data);
    }

    private searchSubmit() {
        if (!this.searchModel.SearchTree || this.searchModel.SearchTree.toQueryString() === "") {
            return;
        }

        if (this.searchModel.SearchTree.HasError) {
            this.searchModel.ErrorMessage = this.searchModel.SearchTree.getError();
            this.searchModel.trigger("ShowOrHideResultsPane");

        } else {
            if (this.searchModel.AdvancedVisible) {
                this.searchModel.MaterialType = this.searchModel.AdvancedSearch.MaterialType;
                this.searchModel.Indicators = this.searchModel.getActiveIndicators();
            }
            this.searchModel.ErrorMessage = "";
            this.searchModel.prepForNewSearch();
            this.searchBoxView.updateExpandedSearchWidgetContent();
            this.searchModel.emitSearchRequest();
        }
    }

    private searchComplete(options: any) {
        this.searchBoxView.placeCaretAtEnd();
    }

    private setupSearchRegionTransitionEvent() {
        const region = this.masterView.getRegion("searchRegion");
        $(region.el).on("transitionend webkitTransitionEnd oTransitionEnd msTransitionEnd", () => {
            const $viewportContent = $("#viewport-content");
            // if the viewport is not showing the advanced search form at the end of the transition, hide it
            if (!$viewportContent.hasClass("advanced-search")) {
                $("#advanced-search-region").hide();
            }
        });
    }

    private setupViews() {
        this.listenToOnce(this.masterView, "show", () => {
            const searchRegion = this.masterView.addRegion("searchRegion", "#search-region");
            searchRegion.show(this.searchView);
            const searchBoxRegion = this.masterView.addRegion("searchBoxRegion", "#search-box-region");
            searchBoxRegion.show(this.searchBoxView);
            this.setupSearchRegionTransitionEvent();
        });
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/search/search-module.ts